'use client';

import { useMemo, useState } from 'react';
import { useOrganization } from '@/lib/OrganizationContext';
import { useMyInvitationsQuery } from '@/hooks/api/useOrganizationMembers';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Mail, Check, X, Building2, Crown, Shield, Users, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

interface InvitationWithOrg {
  id: string;
  organization_id: string;
  role: 'admin' | 'member' | 'coach' | 'player';
  organization: {
    id: string;
    name: string;
    slug: string;
  };
}

function getRoleDisplay(role: string) {
  switch (role) {
    case 'owner':
      return {
        icon: Crown,
        color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        label: 'Propietario',
        bgGradient: 'from-yellow-500/10 to-orange-500/10',
      };
    case 'admin':
      return {
        icon: Shield,
        color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        label: 'Administrador',
        bgGradient: 'from-blue-500/10 to-indigo-500/10',
      };
    case 'member':
      return {
        icon: Users,
        color: 'bg-green-500/20 text-green-400 border-green-500/30',
        label: 'Miembro',
        bgGradient: 'from-green-500/10 to-emerald-500/10',
      };
    case 'player':
      return {
        icon: Users,
        color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
        label: 'Jugador',
        bgGradient: 'from-cyan-500/10 to-sky-500/10',
      };
    default:
      return {
        icon: Users,
        color: 'bg-primary/20 text-primary border-primary/30',
        label: 'Miembro',
        bgGradient: 'from-primary/10 to-primary/5',
      };
  }
}

export const InvitationsList = () => {
  const { acceptInvitation } = useOrganization();
  const { data, isLoading } = useMyInvitationsQuery(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const invitations = useMemo<InvitationWithOrg[]>(() => {
    return (data ?? [])
      .filter((invitation) => !dismissedIds.has(invitation.id))
      .map((invitation) => ({
        id: invitation.id,
        organization_id: invitation.organization_id,
        role: invitation.role,
        organization: {
          id: invitation.organization_id,
          name: invitation.organization_name,
          slug: invitation.organization_slug,
        },
      }));
  }, [data, dismissedIds]);

  const handleAcceptInvitation = async (invitationId: string) => {
    try {
      setProcessingId(invitationId);
      await acceptInvitation(invitationId);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error('No se pudo aceptar la invitación');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(invitationId);
      return next;
    });
    toast.info('La API actual no expone rechazo explícito; se ocultó localmente.');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-24 bg-gray-200 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-2xl blur-xl" />
        <div className="relative bg-card/30 backdrop-blur-xl border border-primary/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-xl blur-md" />
                <div className="relative bg-primary/10 backdrop-blur-xl border border-primary/20 p-3 rounded-xl">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                  Invitaciones Pendientes
                  <Sparkles className="h-5 w-5 text-primary" />
                </h3>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Tienes {invitations.length} invitación
                  {invitations.length !== 1 ? 'es' : ''} esperando tu respuesta
                </p>
              </div>
            </div>
            <Badge className="bg-primary/20 text-primary border-primary/30 px-4 py-2 text-lg font-bold">
              {invitations.length}
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {invitations.map((invitation) => {
          const roleDisplay = getRoleDisplay(invitation.role);
          const RoleIcon = roleDisplay.icon;

          return (
            <Card
              key={invitation.id}
              className="group relative overflow-hidden bg-card/30 backdrop-blur-xl border-primary/10 hover:border-primary/20 transition-all duration-500 hover:shadow-lg hover:shadow-primary/5"
            >
              <div
                className={`absolute inset-0 bg-gradient-to-r ${roleDisplay.bgGradient} opacity-50`}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

              <CardHeader className="relative pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <div className="absolute inset-0 bg-primary/20 rounded-xl blur-sm" />
                      <div className="relative bg-primary/10 backdrop-blur-xl border border-primary/20 p-3 rounded-xl">
                        <Building2 className="h-8 w-8 text-primary" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <CardTitle className="text-xl font-bold text-foreground flex items-center gap-2">
                        {invitation.organization.name}
                        <div className="h-2 w-2 bg-emerald-400 rounded-full animate-pulse" />
                      </CardTitle>

                      <div className="flex items-center space-x-3">
                        <Badge
                          className={`${roleDisplay.color} flex items-center gap-2 px-3 py-1.5 text-sm font-semibold`}
                        >
                          <RoleIcon className="h-4 w-4" />
                          {roleDisplay.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="relative pt-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground/80 max-w-md">
                    Has sido invitado/a a unirte como{' '}
                    <span className="font-semibold text-foreground">{roleDisplay.label}</span> en
                    esta organización. ¿Qué te gustaría hacer?
                  </p>

                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={() => handleAcceptInvitation(invitation.id)}
                      disabled={processingId === invitation.id}
                      className="group bg-emerald-500/20 backdrop-blur-xl border border-emerald-500/30 hover:bg-emerald-500/30 hover:border-emerald-500/50 text-emerald-400 transition-all duration-300 hover:scale-105"
                    >
                      <Check className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                      {processingId === invitation.id ? 'Aceptando...' : 'Aceptar'}
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => handleDeclineInvitation(invitation.id)}
                      disabled={processingId === invitation.id}
                      className="group bg-red-500/10 backdrop-blur-xl border border-red-500/20 hover:bg-red-500/20 hover:border-red-500/40 text-red-400 transition-all duration-300 hover:scale-105"
                    >
                      <X className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                      Rechazar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
